/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/config.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import axios from "axios";
import { AppError, ErrorCode, parseApiError } from "@/types/errors";
import { API_VERSION } from "@/config/enums";
import { AUTH_STORAGE_KEYS, useAuthStore } from "@/store/auth";

// ─────────────────────────────────────────────────────────────────────────────
// CANONICAL config single-source (S109 config unification).
// API_BASE_URL + WS_BASE_URL resolve ONLY from VITE_API_BASE_URL / VITE_WS_BASE_URL.
// Every other module MUST import from here — do NOT re-resolve env in place.
// Env values come from the root `.env` (docker build-args) or a shell-sourced
// `.env` for local dev; there is no separate `frontend/.env`.
//
// Finalize (Hằng, S109 / DD-0069): the default is EMPTY = same-origin (relative).
// Production ships FE + API on ONE origin (bot.nhatquangholding.com); the bundle
// calls relative `/api/v1` and nginx routes it. An env-specific default (e.g.
// http://localhost:7200) baked into prod-path code would mask a misconfig, so it
// is NOT used. Set VITE_API_BASE_URL only for cross-origin / direct-backend dev.
//   - VITE_API_BASE_URL unset OR ""  → current origin (same-origin, prod default)
//   - VITE_API_BASE_URL set          → that absolute base (cross-origin dev)
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_API_BASE_URL = "";

const configuredApiUrl = import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL;
export const API_BASE_URL = configuredApiUrl === ""
  ? `${window.location.protocol}//${window.location.host}`
  : configuredApiUrl;

// WebSocket base — canonical VITE_WS_BASE_URL. If unset, derive from API_BASE_URL
// (http→ws, https→wss). Consumers append their own path (e.g. `/ws/${id}`).
export const WS_BASE_URL = (import.meta.env.VITE_WS_BASE_URL ?? "") !== ""
  ? (import.meta.env.VITE_WS_BASE_URL as string)
  : API_BASE_URL.replace(/^http/, "ws");

// Re-export API_VERSION from enums for backward compatibility
export { API_VERSION };

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public data?: any,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

// #1271: the server answers 403 {detail:{code:"password_change_required"}} on
// every route except change-password/logout/me while the account still has a
// temporary password. Shared by apiRequest() and both axios interceptors.
export function isPasswordChangeRequired(status: number | undefined, data: any): boolean {
  return status === 403 && data?.detail?.code === "password_change_required";
}

/**
 * Sends the browser to /change-password; returns true when it did.
 * Review F-4: only while the store is logged in and never from /login or
 * /change-password — a stale flagged token in localStorage on a logged-out
 * store would otherwise loop /login → 403 → /change-password → RequireAuth
 * → /login (#1264 reload).
 */
export function redirectIfPasswordChangeRequired(status: number | undefined, data: any): boolean {
  if (!isPasswordChangeRequired(status, data)) return false;
  if (!useAuthStore.getState().isAuthenticated) return false;
  const path = window.location.pathname;
  if (path === "/login" || path === "/change-password") return false;
  window.location.assign("/change-password");
  return true;
}

interface RequestOptions extends RequestInit {
  token?: string;
  _isRetry?: boolean; // internal flag — prevents infinite refresh loop
}

// #1264: pages that must stay put on a 401. Redirecting to /login while already
// on /login (or any other public auth route) reloads the page, which fires the
// same stale-token request again → endless reload loop.
const PUBLIC_AUTH_ROUTES = /^\/(login|change-password|platform-admin\/login|auth\/sso(\/|$))/;

/**
 * The ONE place a dead session is handled: drop stale tokens, then send the user
 * to /login unless they are already on a public auth route.
 * Used by every 401/refresh-failure handler (config.ts x2, client.ts x1).
 */
export function clearSessionAndRedirectToLogin(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (PUBLIC_AUTH_ROUTES.test(window.location.pathname)) return;
  window.location.href = "/login";
}

// Module-level singleton so concurrent 401 responses share one refresh call.
let _refreshPromise: Promise<string> | null = null;

// bot-066 (GH#884): exported so client.ts's apiClient/apiClientV2 interceptor
// (tenant-user branch) can share this exact in-flight promise instead of
// running its own independent refresh — see client.ts refreshAccessToken()
// for why a second, uncoordinated singleton caused spurious 401s.
export async function _tryRefreshToken(): Promise<string> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) throw new Error("no_refresh_token");

    const resp = await fetch(`${API_BASE_URL}/api/v1/tenant-users/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!resp.ok) throw new Error("refresh_failed");

    const data = await resp.json();
    const newAccess: string = data.access_token;
    const newRefresh: string = data.refresh_token ?? refreshToken;

    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, newAccess);
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefresh);

    // Sync zustand persist store so next reads get fresh tokens
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.state) {
          parsed.state.accessToken = newAccess;
          parsed.state.refreshToken = newRefresh;
          localStorage.setItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE, JSON.stringify(parsed));
        }
      }
    } catch {
      // Best-effort zustand sync — not critical
    }

    return newAccess;
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { token, ...fetchOptions } = options;

  const url = `${API_BASE_URL}${API_VERSION}${endpoint}`;

  // Debug logging for non-status endpoints
  if (endpoint.includes("attendance") && !endpoint.includes("status")) {
    // Token check for attendance endpoints
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type to JSON if not explicitly provided in options
  if (!options.headers || !('Content-Type' in options.headers)) {
    headers["Content-Type"] = "application/json";
  }

  // Sprint 52 (S52-006): Read tenantKey from auth store (CTO G1)
  // Platform admin endpoints and login endpoints skip X-Tenant-Key
  if (!endpoint.includes("/platform-admin") && !endpoint.includes("/tenant-users/login")) {
    const stored = localStorage.getItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE);
    let tenantKey: string | null = null;
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        tenantKey = parsed?.state?.tenantKey;
      } catch {
        // Ignore parse errors
      }
    }
    if (tenantKey) {
      headers["X-Tenant-Key"] = tenantKey;
    }
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    // Debug logging

    
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });


    // 204 No Content has no body — return early before attempting JSON parse
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    const data = await response.json();

    if (!response.ok) {
      // Auto-refresh: 401 + not already a retry → try token refresh then replay
      if (response.status === 401 && !options._isRetry) {
        try {
          const newToken = await _tryRefreshToken();
          return apiRequest<T>(endpoint, { ...options, token: newToken, _isRetry: true });
        } catch {
          // Refresh failed — clear tokens and redirect to login (unless already on a public auth route, #1264)
          clearSessionAndRedirectToLogin();
          return undefined as unknown as T;
        }
      }

      redirectIfPasswordChangeRequired(response.status, data); // #1271

      const errorCode = parseApiError(response.status, data);
      // detail can be string, object {error, message}, or array [{type, msg, loc}]
      const rawDetail = data.detail;
      const detailMsg = typeof rawDetail === 'string' ? rawDetail
        : Array.isArray(rawDetail) ? rawDetail.map((e: any) => e?.msg || '').filter(Boolean).join(', ') || "Validation error"
        : rawDetail?.message ?? rawDetail?.error ?? "An error occurred";
      throw new AppError(
        errorCode,
        detailMsg,
        response.status,
        data,
      );
    }

    // Only log non-status responses
    if (!endpoint.includes("status")) {
    }
    return data;
  } catch (_error) {

    if (_error instanceof AppError) {
      throw _error;
    }

    // Handle network errors
    if (_error instanceof TypeError) {
      throw new AppError(
        ErrorCode.NETWORK_ERROR,
        "Network error: " + _error.message,
        0,
      );
    }

    throw new AppError(
      ErrorCode.UNKNOWN,
      _error instanceof Error ? _error.message : "Unknown error",
      0,
    );
  }
}

// Create axios instance for AI dashboard
export const api = axios.create({
  baseURL: API_BASE_URL + API_VERSION,
  headers: {
    "Content-Type": "application/json",
  },
});

// BUG-010 fix: V2 axios instance for /api/v2 endpoints (manager dashboard, etc.)
// Prevents double prefix: baseURL /api/v1 + path /api/v2/... = /api/v1/api/v2/... (404)
const API_VERSION_V2 = "/api/v2";
export const apiV2 = axios.create({
  baseURL: API_BASE_URL + API_VERSION_V2,
  headers: {
    "Content-Type": "application/json",
  },
  // BUG THẬT đã sửa (2026-09-08, "Tìm nhân viên" trên Chấm công Manager không
  // lọc): axios mặc định serialize mảng param (vd employee_ids: string[])
  // thành `employee_ids[]=...&employee_ids[]=...` (có ngoặc vuông). FastAPI's
  // `Query(list[UUID])` (backend/app/api/v2/endpoints/manager_attendance.py)
  // chỉ nhận đúng key LẶP TRẦN `employee_ids=...&employee_ids=...` — param có
  // ngoặc vuông không khớp tên, backend nhận None, lặng lẽ bỏ qua filter
  // (không lỗi vì param là Optional). `indexes: null` tắt hẳn ngoặc vuông,
  // khớp định dạng FastAPI mong đợi.
  paramsSerializer: { indexes: null },
});

// Add auth interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Sprint 52 (S52-006): Read tenantKey from auth store (CTO G1)
    if (!config.url?.includes("platform-admin") && !config.url?.includes("tenant-users/login")) {
      const stored = localStorage.getItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE);
      let tenantKey: string | null = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          tenantKey = parsed?.state?.tenantKey;
        } catch {
          // Ignore parse errors
        }
      }
      if (tenantKey) {
        config.headers["X-Tenant-Key"] = tenantKey;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Shared 401 refresh-and-retry response interceptor — reuses the same
// _tryRefreshToken() singleton as apiRequest() so a token refresh triggered
// by a fetch call and one triggered by an axios call collapse into one
// network request instead of racing each other.
// Trước đây `api`/`apiV2` không có bước này ⇒ sau khi access token hết hạn
// (900s), MỌI request Manager 401 rồi rethrow ngay lập tức → mỗi trang
// Manager hiện "Không tải được dữ liệu. Thử lại" thay vì tự làm mới token.
const createAxiosRefreshInterceptor = (client: typeof api) =>
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await _tryRefreshToken();
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        } catch {
          clearSessionAndRedirectToLogin(); // #1264
          return new Promise(() => {}); // navigation is in flight; never resolve
        }
      }

      if (error.response) {
        redirectIfPasswordChangeRequired(error.response.status, error.response.data); // #1271
        const errorCode = parseApiError(
          error.response.status,
          error.response.data,
        );
        // FastAPI 422/400 responses often send `detail` as an OBJECT
        // ({message, violations}), not a string (e.g. manager_schedule.py's
        // "Chặn" mode: {"message": "...", "violations": [...]}). Passing that
        // object straight through as AppError's `message` broke every
        // consumer that renders `error.message` directly — they'd get
        // "[object Object]" or silently fall back to a generic string,
        // which is exactly what showed up as "Thao tác không thành công.
        // Thử lại" on the schedule grid instead of the real violation text.
        // The original detail object is still fully preserved on `context`
        // (4th arg below) for callers that need `violations` too — see
        // extractApiError() in pages/manager/schedule/scheduleShared.ts.
        const rawDetail = error.response.data?.detail;
        const message =
          typeof rawDetail === "string"
            ? rawDetail
            : rawDetail && typeof rawDetail === "object" && typeof rawDetail.message === "string"
              ? rawDetail.message
              : "An error occurred";
        throw new AppError(
          errorCode,
          message,
          error.response.status,
          error.response.data,
        );
      }
      throw new AppError(ErrorCode.NETWORK_ERROR, "Network error", 0);
    },
  );

// BUG-010: Apply same auth + tenant interceptors to apiV2
apiV2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!config.url?.includes("platform-admin") && !config.url?.includes("tenant-users/login")) {
      const stored = localStorage.getItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE);
      let tenantKey: string | null = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          tenantKey = parsed?.state?.tenantKey;
        } catch {
          // Ignore parse errors
        }
      }
      if (tenantKey) {
        config.headers["X-Tenant-Key"] = tenantKey;
      } else {
        // #707/BOT-018 diagnostic (DIAGNOSTIC ONLY — does not change behavior; the
        // request still goes out with no X-Tenant-Key header exactly as before).
        // Intermittent manager 403 "Bạn không có quyền xem nội dung này." on
        // /manager/schedule/* is unreproducible from static reading — this
        // surfaces the FE-side half of the picture so a future repro can tell
        // "header never made it into the request" (FE — e.g. zustand persist
        // store not yet rehydrated from localStorage at request time) apart
        // from "header was present but the BE role-check still failed" (see
        // require_read() WARNING log in backend/app/api/v2/tenant_admin/deps.py).
        console.warn("[#707/BOT-018] apiV2 request sent WITHOUT X-Tenant-Key header", {
          url: config.url,
          method: config.method,
          hasStoredAuth: !!stored,
        });
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

createAxiosRefreshInterceptor(api);
createAxiosRefreshInterceptor(apiV2);
